<template>
  <div class="yhc-ef-datetime-inputs--inner-item--pickup-date-info">
    <div class="yhc-ef-datetime-inputs--inner-item--pickup-date-info--label">お預かり日</div>
    <div class="yhc-ef-datetime-inputs--inner-item--pickup-date-info--dates">
      {{ $yhcefs.$userInputData.pickupDateDisplayLabel }}
      <!-- リセットフラグがtrueの場合に警告メッセージを表示 -->
      <div
        v-if="$yhcefs.$userInputData.pickupDateResetFlag"
        class="pickup-date-warning"
      >
        ※お預かり日を選択してください
      </div>
      <div v-show="canCalcDate || $yhcefs.$userInputData.pickupDateResetFlag">
        <a
          href="#"
          class="d-block hover-fade"
          ref="pickupDateDisplay"
          @click.prevent="() => (showDateSelectPopover = !showDateSelectPopover)"
          style="font-size: 13px; color: var(--yhc-color)"
        >
          <ficon
            type="calendar"
            style="font-size: 1.2em"
          />
          お預かり日を変更
        </a>
        <b-popover
          :target="() => $refs.pickupDateDisplay"
          ref="pickupDatePopover"
          hide-footer
          v-model="showDateSelectPopover"
          placement="bottom"
          custom-class="popover-body-no-padding"
          :manual="true"
          title=""
        >
          <datepicker
            class="border-0"
            :model-value="
              $yhcefs.$userInputData.pickupSelectedDate ||
              $yhcefs.$userInputData.minPickupNeedDate
            "
            @update:model-value="onPickupDateSelected"
            :inline="true"
            :min-date="$yhcefs.$userInputData.minPickupNeedDate"
            :disabled-dates="disabledDates"
          />
        </b-popover>
      </div>
    </div>
    <div
      v-if="canCalcDate && !isSameDayDelivery && !hasActivePeakSeasonRule"
      class="yhc-ef-datetime-inputs--inner-item--pickup-date-info--desc"
    >
      当日18時までの事前予約で、最短翌日のお預かりが可能です。<br />
      18時以降のご予約は、翌々日からのお預かりとなります。<br />
      引越シーズン期間や
      Web申し込みの場合は、最短の日付よりもお預かり・配達にプラス1日いただきます。<br />
      ※一部エリアでは定休日を設けております。詳しくはお預かり日変更よりカレンダーをご確認ください。
    </div>
    <div
      v-if="hasActivePeakSeasonRule"
      class="yhc-ef-datetime-inputs--inner-item--pickup-date-info--desc"
    >
      <span class="warning"
        >{{ formatDateForMD(deliveryPeakSeasonRule.startDate) }} ～
        {{ formatDateForMD(deliveryPeakSeasonRule.endDate) }}</span
      >は引越シーズン期間につき多くのお客様にご利用いただいております。
      すべてのお客様に変わらぬサービス品質をお届けするため、以下の対応とさせていただきます。
      <ul>
        <li>
          シーズン加算料金として<span class="warning"
            >+ ¥{{ deliveryPeakSeasonRule.additionalPrice.toLocaleString() }}</span
          >
          (税込) /件が発生します。
        </li>
        <li><span class="warning">時間帯指定はできません。</span></li>
        <li v-if="hasDeliverableItemCountsLimitForRule">
          同一日に同一住所の配送可能数
          <span class="warning"
            >{{ deliveryPeakSeasonRule.deliverableItemCounts }}点 </span
          >までとなります。
        </li>
        <li>当日15時までの事前予約で、最短翌々日のお預かりが可能です。</li>
        <li v-if="deliveryPeakSeasonRule.sameDayDeliveryDisabled">
          お預かり日当日の配達はご利用いただけません。
        </li>
      </ul>
    </div>
    <div
      v-if="pickupWorkingDaysChangeNote"
      class="pickup-date-warning"
    >
      {{ pickupWorkingDaysChangeNote }}
    </div>
  </div>
</template>
<script lang="ts">
import { YhcEstimateFormFrontService } from '../../YhcEstimateFormFrontService'
import { ModelYhcKazaiDeliveryPeakSeasonRules } from '../../../../fireModels/yhcDataTypes'
import {
  hasDeliverableItemCountsLimit,
  hasPeakSeasonRule,
} from '../peakSeasonRuleUtils'
import { formatDateForMD } from './dateFormatter'

/**
 * お預かり日 の 算出 と表示を実施する
 */
export default {
  name: 'YhcEfDatetimePickupDate',
  inject: ['YhcEstimateFormFrontServiceInstance'],
  setup() {
    return {
      formatDateForMD,
    }
  },
  data() {
    return {
      showDateSelectPopover: false,
    }
  },
  computed: {
    $yhcefs(): YhcEstimateFormFrontService {
      return this.YhcEstimateFormFrontServiceInstance
    },
    canCalcDate(): boolean {
      return (
        this.$yhcefs.$userInputData.pickupDateDisplayLabel.indexOf('年') >= 0 ||
        this.$yhcefs.$userInputData.pickupDateResetFlag
      )
    },
    isSameDayDelivery(): boolean {
      return this.$yhcefs.$userInputData.isSameDayPickupAndDelivery
    },
    deliveryPeakSeasonRule(): ModelYhcKazaiDeliveryPeakSeasonRules | undefined {
      // お預かり日～お届け日が繁忙期シーズンルールの開始日～終了日に含まれていれば、そのルールマスタを返す
      return this.$yhcefs.$userInputData.deliveryPeakSeasonRule
    },
    hasActivePeakSeasonRule(): boolean {
      return hasPeakSeasonRule(this.deliveryPeakSeasonRule)
    },
    hasDeliverableItemCountsLimitForRule(): boolean {
      return hasDeliverableItemCountsLimit(this.deliveryPeakSeasonRule)
    },
    pickupWorkingDaysChangeNote(): string | null {
      const uid = this.$yhcefs.$userInputData
      void this.$yhcefs.masterVersionPeriods
      void uid.fromWorkingDayPatternByVersionId
      void uid.regularWorkingDayRecords
      void uid.zip_from
      void this.$yhcefs.isFleaMarket
      void this.$yhcefs.baseDatetime
      return uid.pickupWorkingDaysChangeNote
    },
    disabledDates() {
      const uid = this.$yhcefs.$userInputData
      void this.$yhcefs.masterVersionPeriods
      void uid.fromWorkingDayPatternByVersionId
      void uid.regularWorkingDayRecords
      void uid.fixed_holidays
      void this.$yhcefs.isFleaMarket
      return (date) => uid.isPickupCalendarDateDisabled(date)
    },
  },
  methods: {
    async onPickupDateSelected(value: string | Date | null) {
      this.$yhcefs.$userInputData.pickupSelectedDate = value
        ? $core.$dayjs(value).format('YYYY-MM-DD')
        : null
      this.$yhcefs.$userInputData.pickupDateResetFlag = false
      await this.$yhcefs.$userInputData.refreshMastersForSelectedDates()
      this.showDateSelectPopover = false
    },
  },
}
</script>

<style lang="scss" scoped>
.warning {
  font-weight: bold;
  color: #ff4500;
}

.pickup-date-warning {
  color: red;
  font-size: 13px;
  margin-top: 5px;
}
</style>
